import { Graphics } from 'pixi.js';
import { BOSS_MECHANICS } from '@/config/boss-mechanics';
import type { BulletSystem } from '@/entities/bullets';
import { orbitFriendly } from '@/entities/bullet-fields';

const H = BOSS_MECHANICS.singularity;
const TAU = Math.PI * 2;

/** Disk look — the body's accretion violet, a white-hot inner edge. */
const LOOK = {
  disk: 0xc77dff,
  hot: 0xffe9ff,
  core: 0xfff8ff,
  /** Spiral streaks wheeling inward over the disk */
  streaks: 4,
  streakLen: 1.3,
  spin: 1.6,
  /** Exposed-core ring (on the horizon edge) + the window countdown arc (px) */
  coreR: 36,
  timerR: 48,
  flicker: 38,
  /** Disk brightness while it flares open */
  flare: 1.6,
  /** Core scale kick per swallowed shot (s of pulse) */
  gulp: 0.12,
} as const;

/** An open arc as its own sub-path (no stray line from the last pen point). */
function arcPath(g: Graphics, r: number, a0: number, a1: number): Graphics {
  return g.moveTo(Math.cos(a0) * r, Math.sin(a0) * r).arc(0, 0, r, a0, a1);
}

/** Accretion spin — matches the clockwise spiral arms (+angularSpeed). */
const SPIN = 1;

/**
 * Singularity's event horizon (docs/02 wave 4). Closed: shots inside the
 * accretion disk orbit and sink into the core (the boss swallows them).
 * Open: the disk collapses and the bare core takes hits for a clear window.
 */
export class Horizon {
  /** The accretion disk — drawn BEHIND the body */
  readonly back = new Graphics();
  /** The open window (core rim + countdown) — drawn OVER the body */
  readonly front = new Graphics();
  /** Time left in the current state (s) */
  private left: number = H.closed;
  private isOpen = false;
  private time = 0;
  /** Core "gulp" pulse when a shot is swallowed */
  private gulp = 0;

  get open(): boolean {
    return this.isOpen;
  }

  /** The disk is about to open — it flares and flickers. */
  private get opening(): boolean {
    return !this.isOpen && this.left < H.warn;
  }

  /** The window is about to close — the disk fades back in. */
  private get closing(): boolean {
    return this.isOpen && this.left < H.closeWarn;
  }

  update(dt: number, cx: number, cy: number, bullets: BulletSystem): void {
    this.time += dt;
    this.gulp = Math.max(0, this.gulp - dt);
    this.left -= dt;
    if (this.left <= 0) {
      this.isOpen = !this.isOpen;
      this.left = this.isOpen ? H.open : H.closed;
    }
    if (!this.isOpen) orbitFriendly(bullets, cx, cy, H.captureR, H.orbitSpeed, H.sink, SPIN);
  }

  /** A captured shot reached the core. */
  swallowed(): void {
    this.gulp = LOOK.gulp;
  }

  /** Core scale kick from swallowing (read by the body sprite). */
  get pulse(): number {
    return 1 + this.gulp;
  }

  render(): void {
    this.back.clear();
    this.front.clear();
    if (!this.isOpen || this.closing) this.drawDisk(this.back);
    if (this.isOpen) this.drawWindow(this.front);
    else if (this.opening && Math.sin(this.time * LOOK.flicker) > 0) {
      // The eye blinks: the horizon edge flashes white before it opens
      this.front.circle(0, 0, LOOK.coreR).stroke({ color: LOOK.core, width: 3, alpha: 0.6 });
    }
  }

  /** The accretion disk: faint sheet, rim and inward spiral streaks. */
  private drawDisk(g: Graphics): void {
    const R = H.captureR;
    const flick = this.opening && Math.sin(this.time * LOOK.flicker) > 0;
    // Fading back in before the window ends; flaring before it opens
    const k = this.closing ? 1 - this.left / H.closeWarn : flick ? LOOK.flare : 1;
    g.circle(0, 0, R).fill({ color: LOOK.disk, alpha: 0.07 * k });
    g.circle(0, 0, R).stroke({ color: flick ? LOOK.hot : LOOK.disk, width: 3, alpha: 0.45 * k });
    const turn = this.time * LOOK.spin * SPIN;
    for (let i = 0; i < LOOK.streaks; i++) {
      const a = turn + (TAU * i) / LOOK.streaks;
      // Each streak curls from the rim toward the core (two arc legs)
      arcPath(g, R * 0.85, a, a + LOOK.streakLen)
        .stroke({ color: LOOK.disk, width: 6, alpha: 0.3 * k, cap: 'round' });
      arcPath(g, R * 0.55, a + LOOK.streakLen * 0.6, a + LOOK.streakLen * 1.5)
        .stroke({ color: LOOK.hot, width: 3, alpha: 0.35 * k, cap: 'round' });
    }
  }

  /** Open: a white-hot exposed core ring + an arc counting the window down. */
  private drawWindow(g: Graphics): void {
    const beat = 0.75 + 0.25 * Math.sin(this.time * 9);
    g.circle(0, 0, LOOK.coreR).stroke({ color: LOOK.hot, width: 7, alpha: 0.35 * beat });
    g.circle(0, 0, LOOK.coreR).stroke({ color: LOOK.core, width: 3, alpha: beat });
    const share = this.left / H.open;
    arcPath(g, LOOK.timerR, -Math.PI / 2, -Math.PI / 2 + TAU * share)
      .stroke({ color: LOOK.core, width: 4, alpha: 0.85, cap: 'round' });
  }
}
